import {MinimalFolder} from 'constants/FolderConstants';
import {REGISTRY_TYPE} from 'constants/SourceConstants';
import {foldersToSelectTreeItems} from 'file-browser/FileSelectorUtil';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import {sortedRootItems} from 'ui-library/select-tree/SelectTreeUtil';
import StrUtil from 'util/strUtil';

/**
 * A project or folder in the omnibox Folder tree. `item` carries the tree structure (keyed
 * by folder apiId, as in the sidepanel folder filter) and `filter` is what selecting it applies: a
 * project's root folder selects the project itself.
 */
export interface FolderTreeNode {
  item: TreeItem;
  filter: OmniboxFilter;
}

export interface FolderTreeRow {
  node: FolderTreeNode;
  depth: number;
  hasChildren: boolean;
}

/**
 * Builds tree nodes from the folders the sidepanel folder filter loads, keeping the same visibility
 * rules: registries and hidden or archived projects are left out.
 */
export function buildFolderTreeNodes(folders: ReadonlyArray<MinimalFolder>): Array<FolderTreeNode> {
  const selectable = folders.filter(
    ({source}) =>
      source != null &&
      source.sourceType !== REGISTRY_TYPE &&
      !source.hidden &&
      !source.archived &&
      !source.archiveRecord
  );
  const foldersById = new Map(selectable.map((folder) => [folder.api_identifier, folder]));
  return foldersToSelectTreeItems(selectable).map((item) => {
    const folder = foldersById.get(item.id)!;
    if (folder.parent == null) {
      return {item, filter: {kind: 'project', id: folder.source!.id, name: folder.name}};
    }
    let root = folder;
    while (root.parent != null) {
      root = root.parent;
    }
    return {
      item,
      filter: {kind: 'folder', id: folder.api_identifier, name: folder.name, projectName: root.name},
    };
  });
}

export interface FolderSearchResult {
  node: FolderTreeNode;
  // Where the match sits, e.g. "Project / Parent", or "Project / ... / Parent" when deeper. Absent
  // for top-level items.
  path?: string;
}

function compactPath(ancestorNames: ReadonlyArray<string>): string | undefined {
  if (ancestorNames.length === 0) {
    return undefined;
  }
  if (ancestorNames.length <= 2) {
    return ancestorNames.join(' / ');
  }
  return `${ancestorNames[0]} / ... / ${ancestorNames[ancestorNames.length - 1]}`;
}

/**
 * Every project or folder whose name contains `query` (case-insensitive, as in the sidepanel folder
 * filter), flattened into one level in tree order so a project's matches stay together, each with
 * its location in the tree.
 */
export function searchFolderTree(
  nodes: ReadonlyArray<FolderTreeNode>,
  query: string
): Array<FolderSearchResult> {
  const nodesById = new Map(nodes.map((node) => [node.item.id, node]));
  const everyId = new Set(nodesById.keys());
  return (
    visibleFolderTreeRows(nodes, everyId)
      // Match the item's own name: for a folder whose parent isn't readable, the tree label is a path
      // that includes ancestor names.
      .filter(({node}) => StrUtil.caseInsensitiveContains(node.filter.name, query))
      .map(({node}) => {
        const ancestorNames: Array<string> = [];
        let parent = node.item.parentId != null ? nodesById.get(node.item.parentId) : undefined;
        while (parent != null) {
          ancestorNames.unshift(parent.item.name);
          parent = parent.item.parentId != null ? nodesById.get(parent.item.parentId) : undefined;
        }
        return {node, path: compactPath(ancestorNames)};
      })
  );
}

/**
 * Flattens the tree into the rows currently visible: roots in the sidepanel's root order, and the
 * children of each expanded node beneath it.
 */
export function visibleFolderTreeRows(
  nodes: ReadonlyArray<FolderTreeNode>,
  expandedIds: ReadonlySet<string>
): Array<FolderTreeRow> {
  const nodesById = new Map(nodes.map((node) => [node.item.id, node]));
  const childrenByParent = new Map<string, Array<FolderTreeNode>>();
  const roots: Array<FolderTreeNode> = [];
  for (const node of nodes) {
    const {parentId} = node.item;
    if (parentId != null && nodesById.has(parentId)) {
      const siblings = childrenByParent.get(parentId) ?? [];
      siblings.push(node);
      childrenByParent.set(parentId, siblings);
    } else {
      roots.push(node);
    }
  }
  const rows: Array<FolderTreeRow> = [];
  const walk = (level: ReadonlyArray<FolderTreeNode>, depth: number): void => {
    for (const node of level) {
      const children = childrenByParent.get(node.item.id) ?? [];
      rows.push({node, depth, hasChildren: children.length > 0});
      if (children.length > 0 && expandedIds.has(node.item.id)) {
        walk(children, depth + 1);
      }
    }
  };
  walk(
    sortedRootItems(roots.map((node) => node.item)).map((item) => nodesById.get(item.id)!),
    0
  );
  return rows;
}
